import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Webhook, Plus, Trash2, RefreshCw, Play, Copy, Loader2, KeyRound,
  AlertTriangle, CheckCircle2, PauseCircle, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import { partnerApi, formatPartnerError, canPartnerAdmin } from "./partnerApi";
import "./PartnerPortal.css";

const STATUS_PILL = {
  delivered: "hw-pp__pill--active",
  pending: "hw-pp__pill--invited",
  failed: "hw-pp__pill--invited",
  dead: "hw-pp__pill--revoked",
};

export default function PartnerWebhooks() {
  const { me } = useOutletContext();
  const canWrite = canPartnerAdmin(me);
  const [subs, setSubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [deliv, setDeliv] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ target_url: "", event_types: [], description: "" });
  const [freshSecret, setFreshSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("subs"); // subs | deliveries

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e, d] = await Promise.all([
        partnerApi.get("/partner/portal/webhooks"),
        partnerApi.get("/partner/portal/webhooks/events"),
        partnerApi.get("/partner/portal/webhooks/deliveries?limit=100"),
      ]);
      setSubs(s.data.subscriptions || []);
      setEvents(e.data.events || []);
      setDeliv(d.data.deliveries || []);
    } catch (e) { toast.error(formatPartnerError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleEvent = (ev) => setForm((f) => ({
    ...f,
    event_types: f.event_types.includes(ev)
      ? f.event_types.filter((x) => x !== ev)
      : [...f.event_types, ev],
  }));

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await partnerApi.post("/partner/portal/webhooks", {
        target_url: form.target_url.trim(),
        event_types: form.event_types,
        description: form.description.trim() || undefined,
      });
      setFreshSecret({ secret: data.secret, subscription_id: data.subscription_id });
      setForm({ target_url: "", event_types: [], description: "" });
      setShowForm(false);
      load();
      toast.success("Webhook created — save the secret NOW.");
    } catch (e) { toast.error(formatPartnerError(e)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (sub) => {
    try {
      await partnerApi.patch(`/partner/portal/webhooks/${sub.subscription_id}`, {
        active: !sub.active,
      });
      toast.success(`Webhook ${sub.active ? "paused" : "resumed"}`);
      load();
    } catch (e) { toast.error(formatPartnerError(e)); }
  };

  const rotate = async (sub) => {
    if (!window.confirm("Rotate the signing secret? The old secret stops working immediately.")) return;
    try {
      const { data } = await partnerApi.post(`/partner/portal/webhooks/${sub.subscription_id}/rotate-secret`);
      setFreshSecret({ secret: data.secret, subscription_id: data.subscription_id });
      toast.success("Secret rotated — save it NOW.");
    } catch (e) { toast.error(formatPartnerError(e)); }
  };

  const verify = async (sub) => {
    try {
      await partnerApi.post(`/partner/portal/webhooks/${sub.subscription_id}/verify`);
      toast.success("Test event queued. Check Delivery History in ~30s.");
      setTab("deliveries");
    } catch (e) { toast.error(formatPartnerError(e)); }
  };

  const del = async (sub) => {
    if (!window.confirm(`Delete this webhook? Deliveries in-flight are cancelled.`)) return;
    try {
      await partnerApi.delete(`/partner/portal/webhooks/${sub.subscription_id}`);
      toast.success("Webhook deleted");
      load();
    } catch (e) { toast.error(formatPartnerError(e)); }
  };

  const retryDelivery = async (id) => {
    try {
      await partnerApi.post(`/partner/portal/webhooks/deliveries/${id}/retry`);
      toast.success("Delivery re-queued");
      load();
    } catch (e) { toast.error(formatPartnerError(e)); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <div data-testid="pp-webhooks">
      <h1 className="hw-pp__page-title">
        <Webhook className="w-5 h-5"
                    style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.4rem" }} />
        Webhooks
      </h1>
      <p className="hw-pp__page-sub">
        Subscribe to real-time events, verify endpoints with test payloads,
        inspect delivery history and DLQ, and rotate signing secrets.
        {!canWrite && " Read-only in your role."}
      </p>

      {freshSecret && (
        <div className="hw-pp__panel" data-testid="pp-webhook-fresh"
              style={{ borderColor: "#F59E0B" }}>
          <h3>
            <span>
              <AlertTriangle className="w-4 h-4"
                              style={{ display: "inline", verticalAlign: "text-bottom",
                                        marginRight: "0.3rem", color: "#F59E0B" }} />
              New signing secret — copy now
            </span>
            <button className="hw-pp__btn" onClick={() => setFreshSecret(null)}>Dismiss</button>
          </h3>
          <div className="hw-pp__reveal-hint">HarborWell will never show this secret again.</div>
          <div className="hw-pp__reveal" data-testid="pp-webhook-fresh-secret">{freshSecret.secret}</div>
          <button className="hw-pp__btn" onClick={() => copy(freshSecret.secret)}>
            <Copy className="w-3.5 h-3.5" /> Copy secret
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: "0.35rem", marginBottom: "0.75rem" }}>
        <button className={`hw-pp__btn ${tab === "subs" ? "hw-pp__btn--primary" : ""}`}
                 onClick={() => setTab("subs")} data-testid="pp-wh-tab-subs">
          Subscriptions ({subs.length})
        </button>
        <button className={`hw-pp__btn ${tab === "deliveries" ? "hw-pp__btn--primary" : ""}`}
                 onClick={() => setTab("deliveries")} data-testid="pp-wh-tab-deliveries">
          Delivery History ({deliv.length})
        </button>
      </div>

      {tab === "subs" && (
        <div className="hw-pp__panel">
          <h3>
            <span>{subs.length} subscription{subs.length === 1 ? "" : "s"}</span>
            <div style={{ display: "flex", gap: "0.35rem" }}>
              <button className="hw-pp__btn" onClick={load}><RefreshCw className="w-3.5 h-3.5" /></button>
              {canWrite && (
                <button className="hw-pp__btn hw-pp__btn--primary" onClick={() => setShowForm(!showForm)}
                         data-testid="pp-wh-new">
                  <Plus className="w-3.5 h-3.5" /> New webhook
                </button>
              )}
            </div>
          </h3>

          {showForm && (
            <div style={{ padding: "0.7rem", background: "rgba(15,23,42,0.5)",
                            borderRadius: "0.5rem", marginBottom: "0.7rem" }}>
              <label className="hw-pp__field">
                <span className="hw-pp__field-label">Target URL (POST payload will be sent here)</span>
                <input className="hw-pp__input" type="url" value={form.target_url}
                        onChange={(e) => setForm({...form, target_url: e.target.value})}
                        placeholder="https://your-service.example/hooks/harborwell"
                        data-testid="pp-wh-target-url" />
              </label>
              <label className="hw-pp__field">
                <span className="hw-pp__field-label">Description (optional)</span>
                <input className="hw-pp__input" value={form.description}
                        onChange={(e) => setForm({...form, description: e.target.value})}
                        data-testid="pp-wh-description" />
              </label>
              <label className="hw-pp__field">
                <span className="hw-pp__field-label">
                  Events to subscribe to ({form.event_types.length}/{events.length})
                </span>
                <div style={{ maxHeight: "160px", overflowY: "auto",
                                padding: "0.35rem 0.5rem",
                                background: "rgba(15,23,42,0.7)",
                                border: "1px solid rgba(94,234,212,0.15)",
                                borderRadius: "0.4rem" }}>
                  {events.map((ev) => (
                    <label key={ev} style={{ display: "block", marginBottom: "0.1rem",
                                                 cursor: "pointer", fontSize: "0.78rem", color: "#CBD5E1" }}>
                      <input type="checkbox" checked={form.event_types.includes(ev)}
                              onChange={() => toggleEvent(ev)}
                              data-testid={`pp-wh-event-${ev}`}
                              style={{ marginRight: "0.35rem" }} />
                      <code style={{ color: "#5EEAD4" }}>{ev}</code>
                    </label>
                  ))}
                </div>
              </label>
              <div style={{ display: "flex", gap: "0.4rem" }}>
                <button className="hw-pp__btn" onClick={() => setShowForm(false)}>Cancel</button>
                <button className="hw-pp__btn hw-pp__btn--primary" onClick={create}
                         disabled={busy || !form.target_url.startsWith("http") || form.event_types.length === 0}
                         data-testid="pp-wh-submit">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  Create webhook
                </button>
              </div>
            </div>
          )}

          {loading && !subs.length ? (
            <div style={{ color: "#5EEAD4" }}>
              <Loader2 className="w-4 h-4 animate-spin" style={{ display: "inline" }} /> loading…
            </div>
          ) : subs.length === 0 ? (
            <div style={{ color: "#64748B", fontSize: "0.85rem", padding: "0.5rem 0" }}>
              No webhooks yet. Subscribe to real-time events by creating one.
            </div>
          ) : (
            <table className="hw-pp__table" data-testid="pp-wh-subs-table">
              <thead>
                <tr>
                  <th>Target URL</th>
                  <th>Events</th>
                  <th>Delivered / Failed</th>
                  <th>Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.subscription_id} data-testid={`pp-wh-sub-${s.subscription_id}`}>
                    <td>
                      <strong style={{ color: "#F1F5F9" }}>{s.target_url}</strong>
                      {s.description && (
                        <div style={{ fontSize: "0.7rem", color: "#64748B" }}>{s.description}</div>
                      )}
                    </td>
                    <td style={{ fontSize: "0.72rem" }}>
                      {(s.event_types || []).slice(0, 3).map((e) => (
                        <span key={e} className="hw-pp__scope-chip">{e}</span>
                      ))}
                      {(s.event_types || []).length > 3 && (
                        <span style={{ color: "#64748B", fontSize: "0.7rem" }}>
                          +{s.event_types.length - 3}
                        </span>
                      )}
                    </td>
                    <td>{s.delivery_count || 0} / {s.failure_count || 0}</td>
                    <td>
                      <span className={`hw-pp__pill ${s.active ? "hw-pp__pill--active" : "hw-pp__pill--revoked"}`}>
                        {s.active ? "Active" : "Paused"}
                      </span>
                    </td>
                    <td>
                      {canWrite && (
                        <>
                          <button className="hw-pp__btn" onClick={() => verify(s)}
                                   title="Send test event"
                                   data-testid={`pp-wh-verify-${s.subscription_id}`}
                                   style={{ padding: "0.2rem 0.4rem" }}>
                            <Play className="w-3 h-3" />
                          </button>
                          <button className="hw-pp__btn" onClick={() => rotate(s)}
                                   title="Rotate secret"
                                   data-testid={`pp-wh-rotate-${s.subscription_id}`}
                                   style={{ padding: "0.2rem 0.4rem", marginLeft: "0.25rem" }}>
                            <KeyRound className="w-3 h-3" />
                          </button>
                          <button className="hw-pp__btn" onClick={() => toggleActive(s)}
                                   title={s.active ? "Pause" : "Resume"}
                                   data-testid={`pp-wh-toggle-${s.subscription_id}`}
                                   style={{ padding: "0.2rem 0.4rem", marginLeft: "0.25rem" }}>
                            {s.active
                              ? <PauseCircle className="w-3 h-3" />
                              : <PlayCircle className="w-3 h-3" />}
                          </button>
                          <button className="hw-pp__btn hw-pp__btn--danger" onClick={() => del(s)}
                                   title="Delete"
                                   data-testid={`pp-wh-delete-${s.subscription_id}`}
                                   style={{ padding: "0.2rem 0.4rem", marginLeft: "0.25rem" }}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "deliveries" && (
        <div className="hw-pp__panel">
          <h3>
            <span>Recent deliveries + Retry queue + DLQ</span>
            <button className="hw-pp__btn" onClick={load}><RefreshCw className="w-3.5 h-3.5" /></button>
          </h3>
          {deliv.length === 0 ? (
            <div style={{ color: "#64748B", fontSize: "0.85rem", padding: "0.5rem 0" }}>
              No deliveries in the last window. Trigger a test event via a subscription&apos;s ▶️ button.
            </div>
          ) : (
            <table className="hw-pp__table" data-testid="pp-wh-deliv-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Event</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Last HTTP</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {deliv.slice(0, 100).map((d) => (
                  <tr key={d.delivery_id} data-testid={`pp-wh-deliv-${d.delivery_id}`}>
                    <td style={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                      {(d.created_at || "").slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="hw-pp__mono">{d.event_type}</td>
                    <td>
                      <span className={`hw-pp__pill ${STATUS_PILL[d.status] || ""}`}>{d.status}</span>
                    </td>
                    <td>{d.attempts}</td>
                    <td>{d.last_response_status || (d.last_error ? "err" : "—")}</td>
                    <td>
                      {(d.status === "dead" || d.status === "failed") && canWrite && (
                        <button className="hw-pp__btn"
                                 style={{ padding: "0.2rem 0.4rem" }}
                                 onClick={() => retryDelivery(d.delivery_id)}
                                 data-testid={`pp-wh-retry-${d.delivery_id}`}>
                          <RefreshCw className="w-3 h-3" /> Retry
                        </button>
                      )}
                      {d.status === "delivered" && (
                        <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#22C55E" }} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
